import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  DeleteOutline as DeleteOutlineIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { Patient } from '../constants/patient';
import { genderLabels, patientListStatusDisplay, patientStatusChipSx } from '../constants/patient';
import { formatPatientAgeSingleLine } from '../utils/patientDateFormat';
import {
  PatientMedicalInfoRequestsValue,
  PatientUnsetDateValue,
} from './patientIntake/PatientReadonlyValues';
import { formatPatientChartMemoText } from '../utils/formatPatientChartMemo';
import {
  formatMedicalDevices,
  labelCareInsurance,
  labelCareLevel,
  labelDisabilityMedicalCert,
  labelIntractableDiseaseCap,
  labelMedicalInsuranceCopay,
  labelMedicalInsuranceIncomeClass,
  labelMedicalInsuranceType,
  formatOtherCareServiceRow,
  formatPharmacyDisplay,
  labelRequest,
} from './patientIntake/patientDetailLabels';
import IntakeSectionCard from './patientIntake/IntakeSectionCard';
import IntakeFormRow from './patientIntake/IntakeFormRow';
import { intakePageMaxWidthSx } from './patientIntake/intakeFormLayout';
import StaffAssigneeDisplay from './StaffAssigneeDisplay';
import PatientStaffMemo from './PatientStaffMemo';
import PatientActionStatusEditor from './PatientActionStatusEditor';
import PatientActionDueDateEditor from './PatientActionDueDateEditor';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import { useAuth } from '../auth/AuthContext';
import { isDoctorOrAdminRole } from '../constants/staff';

/** 病名・機器と医療保険情報を横並び（入力画面と同じ） */
const pairedRowSection = { xs: 12, md: 6, lg: 6 };

const TextValue: React.FC<{ value: string | null | undefined; multiline?: boolean }> = ({
  value,
  multiline,
}) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: '0.8125rem',
      lineHeight: 1.35,
      whiteSpace: multiline ? 'pre-wrap' : undefined,
      wordBreak: 'break-word',
      color: value?.trim() ? 'text.primary' : 'text.secondary',
    }}
  >
    {value?.trim() ? value : '—'}
  </Typography>
);

const DetailRow: React.FC<{
  label: string;
  children: React.ReactNode;
  align?: 'center' | 'flex-start';
}> = ({ label, children, align = 'center' }) => (
  <IntakeFormRow label={label} align={align} compact>
    {children}
  </IntakeFormRow>
);

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const { staffProfile } = useAuth();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('患者IDが不正です');
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiClient.get<Patient>(`/api/patients/${id}`);
        setPatient(res.data);
      } catch {
        setError('患者情報の取得に失敗しました');
        setPatient(null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id]);

  const handleArchive = async () => {
    if (!id) return;
    setActionLoading(true);
    setActionError('');
    try {
      const res = await apiClient.post<Patient>(`/api/patients/${id}/archive`);
      setPatient(res.data);
      setArchiveDialogOpen(false);
      showSnackbar({ message: 'カルテ登録済みにしました', severity: 'success' });
    } catch {
      setActionError('アーカイブに失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnarchive = async () => {
    if (!id) return;
    setActionLoading(true);
    setActionError('');
    try {
      const res = await apiClient.post<Patient>(`/api/patients/${id}/unarchive`);
      setPatient(res.data);
      setUnarchiveDialogOpen(false);
      showSnackbar({ message: '受付完了に戻しました', severity: 'success' });
    } catch {
      setActionError('アーカイブ解除に失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyChartMemo = async () => {
    if (!patient) return;
    const text = formatPatientChartMemoText(patient);
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar({ message: 'カルテ用テキストをコピーしました', severity: 'success' });
    } catch {
      showSnackbar({ message: 'コピーに失敗しました', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiClient.delete(`/api/patients/${id}`);
      setDeleteDialogOpen(false);
      showSnackbar({ message: '患者を削除しました', severity: 'success' });
      navigate('/patients');
    } catch {
      setActionError('患者の削除に失敗しました');
      setDeleteDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDt = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('ja-JP');
  };

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !patient) {
    return (
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          患者情報
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || '患者が見つかりません'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/patients')}>
          患者一覧へ
        </Button>
      </Box>
    );
  }

  const archived = Boolean(patient.archived_at);
  const editable = patient.status !== 'chart_registered';
  const memoReadOnly = Boolean(staffProfile?.is_developer);
  const canDeletePatient = isDoctorOrAdminRole(staffProfile?.role);
  const diagnoses = patient.diagnoses?.filter((d) => d.trim()) ?? [];
  const statusDisplay = patientListStatusDisplay(patient);

  const metaParts = [
    patient.gender ? genderLabels[patient.gender] : null,
    formatPatientAgeSingleLine(patient.date_of_birth),
  ].filter((v): v is string => Boolean(v?.trim()));

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', width: '100%', pb: 2, pr: { xs: 0.75, lg: 0 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'stretch' },
          mb: 1.5,
          gap: 1,
          ...intakePageMaxWidthSx,
        }}
      >
        <Box sx={{ minWidth: 0, maxWidth: 475, width: '100%' }}>
          <Paper
            variant="outlined"
            sx={{
              width: '100%',
              maxWidth: 475,
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
              borderRadius: 1.5,
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                {patient.patient_number?.trim() ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: '0.8125rem', lineHeight: 1.4, mb: 0.25 }}
                  >
                    {patient.patient_number}
                  </Typography>
                ) : null}
                <Typography
                  variant="h6"
                  component="h1"
                  noWrap
                  sx={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.3 }}
                >
                  {patient.name?.trim() || '（名称未設定）'}
                </Typography>
                {patient.name_kana?.trim() ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ mt: 0.25, fontSize: '0.8125rem' }}
                  >
                    {patient.name_kana}
                  </Typography>
                ) : null}
              </Box>
              <Chip
                label={statusDisplay.label}
                variant="outlined"
                size="small"
                sx={{ flexShrink: 0, ...patientStatusChipSx }}
                color={statusDisplay.chipColor}
              />
            </Box>
            {metaParts.length > 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75, fontSize: '0.8125rem', lineHeight: 1.4 }}
              >
                {metaParts.join(' · ')}
              </Typography>
            ) : null}
            <Box sx={{ mt: 0.75 }}>
              <TextValue value={patient.address} multiline />
            </Box>
          </Paper>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            minHeight: { sm: '100%' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              alignSelf: { sm: 'flex-end' },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => void handleCopyChartMemo()}
            >
              カルテ用コピー
            </Button>
            {editable && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/patients/${patient.id}/edit`)}
              >
                編集
              </Button>
            )}
            {!archived ? (
              <Button
                color="warning"
                variant="outlined"
                size="small"
                startIcon={<ArchiveIcon />}
                onClick={() => setArchiveDialogOpen(true)}
              >
                カルテ登録済みにする
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<UnarchiveIcon />}
                onClick={() => setUnarchiveDialogOpen(true)}
              >
                受付完了に戻す
              </Button>
            )}
            {canDeletePatient && (
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                削除
              </Button>
            )}
          </Box>
          <Box
            sx={{
              alignSelf: 'flex-start',
              mt: { xs: 1.5, sm: 'auto' },
              pl: { sm: 2.5 },
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <PatientActionStatusEditor
              patientId={patient.id}
              initialValue={patient.action_status ?? 'none'}
              readOnly={memoReadOnly}
              onSaved={(action_status) =>
                setPatient((prev) => (prev ? { ...prev, action_status } : prev))
              }
            />
            <PatientActionDueDateEditor
              patientId={patient.id}
              initialValue={patient.action_due_date ?? null}
              readOnly={memoReadOnly}
              onSaved={(action_due_date) =>
                setPatient((prev) => (prev ? { ...prev, action_due_date } : prev))
              }
            />
          </Box>
        </Box>
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.25, ...intakePageMaxWidthSx }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      <Box sx={intakePageMaxWidthSx}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 1.5,
            alignItems: { lg: 'flex-start' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Grid container spacing={1.5}>
        <IntakeSectionCard title="依頼・初診" compact>
          <DetailRow label="依頼日">
            <TextValue value={patient.requested_on?.slice(0, 10)} />
          </DetailRow>
          <DetailRow label="訪問開始の目安">
            <TextValue value={patient.visit_start_estimate} />
          </DetailRow>
          <DetailRow label="初診日">
            <PatientUnsetDateValue
              iso={patient.first_visit_date}
              dateFormat="iso"
              compact
            />
          </DetailRow>
          <DetailRow label="初診日同席者" align="flex-start">
            <TextValue value={patient.first_visit_attendees} multiline />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="紹介元" compact>
          <DetailRow label="紹介元">
            <TextValue value={patient.referral_source?.name} />
          </DetailRow>
          <DetailRow label="紹介元電話">
            <TextValue value={patient.referral_phone} />
          </DetailRow>
          <DetailRow label="担当者名">
            <TextValue value={patient.referral_contact_name} />
          </DetailRow>
          <DetailRow label="依頼の経緯" align="flex-start">
            <TextValue value={patient.request_background} multiline />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="書類依頼" size={pairedRowSection} compact>
          <DetailRow label="フェイスシート依頼">
            <TextValue value={labelRequest(patient.face_sheet_request)} />
          </DetailRow>
          <DetailRow label="ケアプラン依頼">
            <TextValue value={labelRequest(patient.care_plan_request)} />
          </DetailRow>
          <DetailRow label="診療情報提供書依頼" align="flex-start">
            <PatientMedicalInfoRequestsValue
              items={patient.medical_info_request}
              compact
            />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="同意書" size={pairedRowSection} compact>
          <DetailRow label="同意書アポ日">
            <PatientUnsetDateValue
              iso={patient.consent_appointment_date}
              dateFormat="iso"
              compact
            />
          </DetailRow>
          <DetailRow label="応対者">
            <TextValue value={patient.consent_appointment_contact} />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="本人・パーキング" compact>
          <DetailRow label="患者電話">
            <TextValue value={patient.patient_phone} />
          </DetailRow>
          <DetailRow label="本人の状態" align="flex-start">
            <TextValue value={patient.patient_condition} multiline />
          </DetailRow>
          <DetailRow label="パーキング" align="flex-start">
            <TextValue value={patient.parking_info} multiline />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="家族" compact>
          <DetailRow label="家族構成" align="flex-start">
            <TextValue value={patient.family_structure} multiline />
          </DetailRow>
          <DetailRow label="キーパーソン">
            <TextValue value={patient.key_person} />
          </DetailRow>
          <DetailRow label="キーパーソン電話">
            <TextValue value={patient.key_person_phone} />
          </DetailRow>
          <DetailRow label="キーパーソン住所" align="flex-start">
            <TextValue value={patient.key_person_address} multiline />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="医療者" compact>
          <DetailRow label="主治医">
            <StaffAssigneeDisplay assignee={patient.attending_physician} size={22} />
          </DetailRow>
          <DetailRow label="業務担当者">
            <StaffAssigneeDisplay assignee={patient.assigned_to} size={22} />
          </DetailRow>
          <DetailRow label="かかりつけ医">
            <TextValue value={patient.family_doctor?.name} />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="病名・機器" size={pairedRowSection} compact>
          {diagnoses.length > 0 ? (
            diagnoses.map((d, i) => (
              <DetailRow key={i} label={i === 0 ? '病名' : `病名${i + 1}`}>
                <TextValue value={d} />
              </DetailRow>
            ))
          ) : (
            <DetailRow label="病名">
              <TextValue value={null} />
            </DetailRow>
          )}
          <DetailRow label="医療機器" align="flex-start">
            <TextValue value={formatMedicalDevices(patient.medical_devices)} multiline />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="医療保険情報" size={pairedRowSection} compact>
          <DetailRow label="種類">
            <TextValue value={labelMedicalInsuranceType(patient.medical_insurance_type)} />
          </DetailRow>
          <DetailRow label="所得区分">
            <TextValue value={labelMedicalInsuranceIncomeClass(patient.medical_insurance_income_class)} />
          </DetailRow>
          <DetailRow label="負担割合">
            <TextValue value={labelMedicalInsuranceCopay(patient.medical_insurance_copay)} />
          </DetailRow>
          <DetailRow label="難病医療証 上限">
            <TextValue value={labelIntractableDiseaseCap(patient.intractable_disease_cap)} />
          </DetailRow>
          <DetailRow label="障害者医療証">
            <TextValue value={labelDisabilityMedicalCert(patient.disability_medical_cert)} />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="介護・サービス" size={{ xs: 12 }} compact>
          <DetailRow label="介護保険">
            <TextValue value={labelCareInsurance(patient.care_insurance_status)} />
          </DetailRow>
          {patient.care_insurance_status === 'certified' && (
            <DetailRow label="介護度">
              <TextValue value={labelCareLevel(patient.care_insurance_level)} />
            </DetailRow>
          )}
          <DetailRow label="居宅介入">
            <TextValue
              value={
                patient.home_care_intervention === null
                  ? null
                  : patient.home_care_intervention
                    ? 'あり'
                    : 'なし'
              }
            />
          </DetailRow>
          {patient.home_care_intervention && (
            <DetailRow label="担当者名">
              <TextValue value={patient.home_care_contact_name} />
            </DetailRow>
          )}
          <DetailRow label="訪問看護" align="flex-start">
            <TextValue
              value={
                patient.visit_nursing?.has_service === undefined
                  ? null
                  : patient.visit_nursing.has_service
                    ? [
                        patient.visit_nursing.office_name,
                        patient.visit_nursing.frequency,
                        patient.visit_nursing.instruction_source,
                        patient.visit_nursing.period_start &&
                          `期間: ${patient.visit_nursing.period_start}〜${patient.visit_nursing.period_end ?? ''}`,
                      ]
                        .filter(Boolean)
                        .join(' / ')
                    : 'なし'
              }
              multiline
            />
          </DetailRow>
          <DetailRow label="訪問リハ" align="flex-start">
            <TextValue
              value={formatOtherCareServiceRow(
                patient.other_care_services ?? {},
                'visit_rehab',
                'visit_rehab_note',
              )}
              multiline
            />
          </DetailRow>
          <DetailRow label="デイサービス" align="flex-start">
            <TextValue
              value={formatOtherCareServiceRow(
                patient.other_care_services ?? {},
                'day_service',
                'day_service_note',
              )}
              multiline
            />
          </DetailRow>
          <DetailRow label="ヘルパー" align="flex-start">
            <TextValue
              value={formatOtherCareServiceRow(
                patient.other_care_services ?? {},
                'helper',
                'helper_note',
              )}
              multiline
            />
          </DetailRow>
          <DetailRow label="その他介護" align="flex-start">
            <TextValue
              value={formatOtherCareServiceRow(
                patient.other_care_services ?? {},
                'other',
                'other_note',
              )}
              multiline
            />
          </DetailRow>
          <DetailRow label="全体備考" align="flex-start">
            <TextValue value={patient.other_care_services?.general_note?.trim() || null} multiline />
          </DetailRow>
          <DetailRow label="かかりつけ薬局" align="flex-start">
            <TextValue
              value={formatPharmacyDisplay(patient.pharmacy_status, patient.pharmacy_name)}
            />
          </DetailRow>
        </IntakeSectionCard>

        <IntakeSectionCard title="特記事項" size={{ xs: 12 }} compact>
          <TextValue value={patient.notes} multiline />
        </IntakeSectionCard>
            </Grid>

            <Box sx={{ mt: 1.5, pt: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, fontSize: '0.6875rem', fontWeight: 600 }}
              >
                履歴
              </Typography>
              <DetailRow label="登録日時">
                <TextValue value={formatDt(patient.created_at)} />
              </DetailRow>
              <DetailRow label="作成者">
                <TextValue value={patient.created_by?.display_name} />
              </DetailRow>
              <DetailRow label="最終更新">
                <TextValue value={formatDt(patient.updated_at)} />
              </DetailRow>
              <DetailRow label="更新者">
                <TextValue value={patient.updated_by?.display_name} />
              </DetailRow>
              {archived && (
                <DetailRow label="カルテ登録日時">
                  <TextValue value={formatDt(patient.archived_at)} />
                </DetailRow>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              width: { xs: '100%', lg: 280 },
              flexShrink: 0,
              boxSizing: 'border-box',
              pr: { xs: 0.5, sm: 0.75, lg: 0 },
            }}
          >
            <PatientStaffMemo
              patientId={patient.id}
              initialValue={patient.staff_memo ?? ''}
              readOnly={memoReadOnly}
              onSaved={(staff_memo) => setPatient((prev) => (prev ? { ...prev, staff_memo } : prev))}
            />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={archiveDialogOpen}
        onClose={() => !actionLoading && setArchiveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>カルテ登録済みにする</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            ステータスを「カルテ登録済み」とし、患者一覧からは通常非表示になります。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setArchiveDialogOpen(false)} disabled={actionLoading} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button
            onClick={() => void handleArchive()}
            color="warning"
            variant="contained"
            disabled={actionLoading}
            sx={{ minWidth: 100 }}
          >
            実行
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unarchiveDialogOpen}
        onClose={() => !actionLoading && setUnarchiveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>受付完了に戻す</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            ステータスを「受付完了」とし、患者一覧に再表示します。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setUnarchiveDialogOpen(false)} disabled={actionLoading} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button
            onClick={() => void handleUnarchive()}
            variant="contained"
            disabled={actionLoading}
            sx={{ minWidth: 100 }}
          >
            実行
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !actionLoading && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>患者を削除</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            「{patient.name?.trim() || '（名称未設定）'}」を削除します。この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button
            onClick={() => void handleDelete()}
            color="error"
            variant="contained"
            disabled={actionLoading}
            sx={{ minWidth: 100 }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDetail;
